import type { RecordedGrpcCall } from '../recording/recordingSink';

/**
 * Pure derivation of the recorder diagnostics log artifacts (`logs/`) that the
 * archive builder bundles. Framework-light, no DOM — a log-artifact provider for
 * the builder (card B extension point).
 *
 * - `logs/recorder.log` — the lifecycle/diagnostics lines (produced by
 *   `RecorderDiagnosticsLog`; passed into the builder as `logLines`).
 * - `logs/grpc.log` — a compact per-call summary derived here from the captured
 *   gRPC calls (method/status/duration/correlationId).
 * - `logs/errors.log` — errors + warnings only, aggregated from the recorder log
 *   (ERROR/WARN lines), failed gRPC calls, and plugin-side warnings.
 */

export const RECORDER_LOG_REF = 'logs/recorder.log';
export const GRPC_LOG_REF = 'logs/grpc.log';
export const ERRORS_LOG_REF = 'logs/errors.log';

/** Collapse whitespace so a value never breaks the space-delimited key=value format. */
function sanitize(value: string | number): string {
  return String(value).replace(/\s+/g, '_');
}

/** Matches a recorder log line at ERROR or WARN level (frozen format: `[recorder] <ts> <LEVEL> ...`). */
const ERROR_OR_WARN = /\s(ERROR|WARN)\s/;

/**
 * One compact line per captured gRPC call:
 * `[grpc] index=<i> method=<m> status=<s> duration_ms=<n> correlationId=<id>`.
 */
export function buildGrpcLogLines(calls: readonly RecordedGrpcCall[]): string[] {
  return calls.map((call) => {
    const parts = ['[grpc]', `index=${call.index}`, `method=${sanitize(call.method)}`];
    if (call.status !== undefined) {
      parts.push(`status=${sanitize(call.status)}`);
    }
    if (call.durationMs !== undefined) {
      parts.push(`duration_ms=${call.durationMs}`);
    }
    if (call.correlationId) {
      parts.push(`correlationId=${sanitize(call.correlationId)}`);
    }
    return parts.join(' ');
  });
}

export interface ErrorsLogInput {
  /** The formatted recorder.log lines (ERROR/WARN are pulled out). */
  recorderLogLines?: readonly string[];
  /** Plugin-side / collection warnings (mirrors scenario.json `warnings`). */
  warnings?: readonly string[];
  /** Captured gRPC calls — non-OK statuses become error entries. */
  grpcCalls?: readonly RecordedGrpcCall[];
}

/**
 * Aggregate every error/warning into one file: recorder ERROR/WARN lines, then
 * plugin/collection warnings, then failed gRPC calls. Returns [] when clean.
 */
export function buildErrorsLogLines(input: ErrorsLogInput): string[] {
  const out: string[] = [];

  for (const line of input.recorderLogLines ?? []) {
    if (ERROR_OR_WARN.test(line)) {
      out.push(line);
    }
  }

  for (const warning of input.warnings ?? []) {
    out.push(`[warning] ${warning}`);
  }

  for (const call of input.grpcCalls ?? []) {
    if (call.status && call.status !== 'OK') {
      const parts = ['[grpc.error]', `method=${sanitize(call.method)}`, `status=${sanitize(call.status)}`];
      if (call.error) {
        parts.push(`detail=${sanitize(call.error)}`);
      }
      if (call.correlationId) {
        parts.push(`correlationId=${sanitize(call.correlationId)}`);
      }
      out.push(parts.join(' '));
    }
  }

  return out;
}
